/**
 * NetworkStatusIndicator Component
 * 
 * Displays network connection status with retry options
 * Validates: Requirements 18.5
 */

import { StatusIndicator, Button, Box } from '@cloudscape-design/components';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import './NetworkStatusIndicator.css';

export interface NetworkStatusIndicatorProps {
  /**
   * Whether to show the indicator inline or as a banner
   */
  variant?: 'inline' | 'banner';
  
  /**
   * Whether to show retry button
   */
  showRetry?: boolean;
  
  /**
   * Callback when retry is clicked
   */
  onRetry?: () => void;
  
  /**
   * Whether to auto-hide when online
   */
  autoHide?: boolean;
}

/**
 * Network status indicator component
 */
export function NetworkStatusIndicator({
  variant = 'inline',
  showRetry = true,
  onRetry,
  autoHide = true,
}: NetworkStatusIndicatorProps) {
  const { isOnline, isBackendReachable, isChecking, refresh } = useNetworkStatus({
    checkBackend: true,
    checkInterval: 30000,
  });

  // Auto-hide when everything is working
  if (autoHide && isOnline && isBackendReachable) {
    return null;
  }

  const handleRetry = () => {
    refresh();
    if (onRetry) {
      onRetry();
    }
  };

  // Determine status
  let status: 'error' | 'warning' | 'success' | 'loading' = 'success';
  let message = 'Connected';

  if (isChecking) {
    status = 'loading';
    message = 'Checking connection...';
  } else if (!isOnline) {
    status = 'error';
    message = 'No internet connection';
  } else if (!isBackendReachable) {
    status = 'warning';
    message = 'Cannot reach server';
  }

  if (variant === 'banner') {
    return (
      <div className={`network-status-banner network-status-banner--${status}`}>
        <Box padding={{ vertical: 's', horizontal: 'm' }}>
          <div className="network-status-banner__content">
            <StatusIndicator type={status}>
              {message}
            </StatusIndicator>
            {showRetry && !isChecking && (
              <Button
                variant="link"
                onClick={handleRetry}
                disabled={isChecking}
              >
                Retry
              </Button>
            )}
          </div>
        </Box>
      </div>
    );
  }

  return (
    <div className="network-status-inline">
      <StatusIndicator type={status}>
        {message}
      </StatusIndicator>
      {showRetry && !isChecking && !isOnline && (
        <Button
          variant="link"
          onClick={handleRetry}
          disabled={isChecking}
          iconName="refresh"
        >
          Retry
        </Button>
      )}
    </div>
  );
}
